import { useEffect, useId, useRef, useState } from 'react';
import {
  type DocumentDTO,
  type DocumentSuggestion,
  type DocumentVisibility,
  type SuggestedPerson,
  vaultNextReviewIso,
} from '@almanac/shared';
import { documentKinds } from '@almanac/shared';
import {
  distinctKinds,
  distinctSubAreas,
  useCreateDocument,
  useDeleteDocument,
  useDocuments,
  useRemoveDocumentFile,
  useRestoreDocument,
  useUpdateDocument,
  useUploadDocumentFile,
} from '../lib/documents.js';
import { api, ApiError, NetworkError } from '../lib/api.js';
import { DOC_FILE_ACCEPT, formatFileSize, isAcceptedDocFile } from '../lib/files.js';
import { useLifeAreas } from '../lib/obligations.js';
import { useCreatePerson } from '../lib/people.js';
import { useUndoToast } from '../lib/undoToast.js';
import { tripDatesLabel, tripPhase, useTrips } from '../lib/travel.js';
import { PersonPicker } from './PersonPicker.js';

/**
 * Create/edit sheet for a document. `target` is `'new'` for a fresh
 * record, an existing `DocumentDTO` to edit, or `null` when closed.
 *
 * Mirrors the obligation EditDrawer's drag-to-dismiss + scroll-lock
 * mechanics so the two sheets feel identical.
 */

// Sentinel select value for the "Other…" escape hatch — never a real
// category or sub-area, so it can't collide with one.
const OTHER_KIND = '__other__';
const OTHER_SUB_AREA = '__other_sub__';

const DEFAULT_AREA_ID = 'area_documents';

interface Props {
  /** Opened from the Vault: new records are born vital and default to
   *  shared — the vault is precisely what the household must see. */
  vault?: boolean;
  /** Opened from a trip page: a fresh record starts linked to that
   *  trip (the Trip field below stays editable). */
  presetTripId?: string;
  target: DocumentDTO | 'new' | null;
  onClose: () => void;
  /** A file staged before the drawer opened — e.g. dropped onto the page
   *  or picked from the capture sheet. Pre-attached to a fresh record. */
  initialFile?: File | null;
}

interface DraftState {
  title: string;
  lifeAreaId: string;
  subArea: string;
  category: string;
  pertainsTo: string[];
  issuer: string;
  issuedDate: string;
  expiryDate: string;
  tags: string;
  notes: string;
  visibility: DocumentVisibility;
  /** "Keep in the Vault" toggle — documentCategory 'vital' on save.
   *  Only a *changed* toggle writes the category, so an untouched
   *  save never flips a Retired record back to routine. */
  vault: boolean;
  /** Review cadence in months (6 / 12 / 24); 12 is the default. */
  reviewMonths: number;
  /** The trip this document belongs to; '' = none. */
  tripId: string;
}

function emptyDraft(lifeAreaId: string, vault?: boolean, tripId?: string): DraftState {
  return {
    tripId: tripId ?? '',
    title: '',
    lifeAreaId,
    subArea: '',
    category: '',
    pertainsTo: [],
    issuer: '',
    issuedDate: '',
    expiryDate: '',
    tags: '',
    notes: '',
    visibility: vault ? 'shared' : 'private',
    vault: Boolean(vault),
    reviewMonths: 12,
  };
}

function toDraft(d: DocumentDTO): DraftState {
  return {
    tripId: d.tripId ?? '',
    vault: d.documentCategory === 'vital',
    reviewMonths: d.vaultReviewMonths ?? 12,
    title: d.title,
    lifeAreaId: d.lifeAreaId,
    subArea: d.subArea ?? '',
    category: d.category ?? '',
    pertainsTo: d.pertainsTo ?? [],
    issuer: d.issuer ?? '',
    issuedDate: (d.issuedDate ?? '').slice(0, 10),
    expiryDate: (d.expiryDate ?? '').slice(0, 10),
    tags: (d.tags ?? []).join(', '),
    notes: d.notes ?? '',
    visibility: d.visibility,
  };
}

function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .slice(0, 20);
}

const DRAG_DISMISS_PX = 200;
const DRAG_CLOSE_ANIMATION_MS = 220;

/** Reject if a promise hasn't settled in `ms` — turns a silent hang
 *  (e.g. a paused mutation) into a visible, labelled error. */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms),
    ),
  ]);
}

/** A sensible default title from a filename: drop the extension, turn
 *  separators into spaces. Lets "attach a file → Save" work without
 *  forcing a title first (and is a fine starting point to edit). */
/** The review panel's info line: when this record was last confirmed
 *  and when the next review falls due under the chosen cadence. */
function reviewHint(existing: DocumentDTO | null, months: number): string {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  if (!existing) {
    return months === 12
      ? 'First review falls due a year after adding.'
      : `First review falls due ${months} months after adding.`;
  }
  const next = fmt(vaultNextReviewIso(existing.vaultReviewedAt ?? existing.createdAt, months));
  return existing.vaultReviewedAt
    ? `Last reviewed ${fmt(existing.vaultReviewedAt)} · next due ${next}.`
    : `Not reviewed yet · first review due ${next}.`;
}

function titleFromFileName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/u, '');
  return base.replace(/[_-]+/gu, ' ').replace(/\s+/gu, ' ').trim().slice(0, 200);
}

/** A short, user-readable cause for the on-screen error. */
function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const detail =
      err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : '';
    return `server ${err.status}${detail ? ` (${detail})` : ''}`;
  }
  if (err instanceof NetworkError) return 'no connection';
  if (err instanceof Error) return err.message;
  return 'unknown error';
}

export function DocumentEditDrawer({ target, onClose, initialFile, vault, presetTripId }: Props) {
  const lifeAreas = useLifeAreas();
  // For the category select: existing documents contribute any custom
  // kinds already in use (cache-served — the page has this query live).
  const documents = useDocuments();
  const create = useCreateDocument();
  const update = useUpdateDocument();
  const upload = useUploadDocumentFile();
  const removeFile = useRemoveDocumentFile();
  const del = useDeleteDocument();
  const restore = useRestoreDocument();
  // Trip select: trips that haven't ended, plus (when editing) the
  // record's current trip even if it's over — so an old link stays
  // visible rather than silently vanishing from the form.
  const trips = useTrips();
  const currentTripId = target && target !== 'new' ? target.tripId : null;
  const tripOptions = (trips.data ?? [])
    .filter((t) => tripPhase(t, new Date()) !== 'past' || t.id === currentTripId)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map((t) => ({ id: t.id, label: `${t.title} · ${tripDatesLabel(t).split(' – ')[0]}` }));
  const createPerson = useCreatePerson();
  const undo = useUndoToast();
  const [draft, setDraft] = useState<DraftState | null>(null);
  // AI read-with-Claude state (Phase 3). `reading` is the in-flight call;
  // `aiFilled` shows the "read from your file" banner once suggestions
  // land; `aiNote` carries a one-off message (not configured / can't read
  // / error); `suggestedPeople` are names Claude found that didn't match
  // an existing person — offered as one-tap add chips.
  const [reading, setReading] = useState(false);
  const [aiFilled, setAiFilled] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const [suggestedPeople, setSuggestedPeople] = useState<SuggestedPerson[]>([]);
  // True while the category select is on "Other…" — the draft's category
  // then comes from a free-text input for the rare kind that isn't in
  // the standard list.
  const [customKind, setCustomKind] = useState(false);
  // Same escape hatch for the sub-area select ("Other… → Dentist").
  const [customSubArea, setCustomSubArea] = useState(false);
  // Inline confirm for permanently removing an already-attached file.
  const [confirmingFileRemove, setConfirmingFileRemove] = useState(false);
  // A newly-picked/dropped file waiting to be uploaded on save. Null
  // means "no change" — the existing attachment (if any) stays.
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileDragOver, setFileDragOver] = useState(false);
  // True while a file is uploading. We keep the drawer mounted during
  // the upload (rather than closing immediately) so the mutation hooks
  // run to completion — closing unmounts them and the upload is lost.
  const [saving, setSaving] = useState(false);
  // Step label shown on the Save button while a file save runs, so a
  // failure can be pinned to the create vs the upload step.
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  // Once a fresh record's create succeeds, remember its id so a retry
  // after an upload failure updates that record rather than creating a
  // duplicate.
  const committedId = useRef<string | null>(null);
  // A real <label htmlFor> opens the OS picker natively on tap — unlike a
  // JS `.click()` on a hidden input, which iOS Safari ignores in
  // standalone PWA mode.
  const fileInputId = useId();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef<number | null>(null);

  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;

  // Prefer the Documents life area for new records, falling back to the
  // first area if the seed id ever changes.
  const defaultAreaId =
    lifeAreas.data?.find((a) => a.id === DEFAULT_AREA_ID)?.id ??
    lifeAreas.data?.[0]?.id ??
    DEFAULT_AREA_ID;

  // The category select's options: the standard vocabulary, plus any
  // custom kinds already in use across the catalogue (added via
  // "Other…"), plus the current draft value if it matches neither — so
  // an existing record never shows an empty select. Deduped
  // case-insensitively and always presented as one alphabetical list,
  // customs interleaved, so a kind is found by name, not provenance.
  const kindOptions = (() => {
    const seen = new Map<string, string>();
    for (const k of documentKinds) seen.set(k.toLowerCase(), k);
    for (const k of [...distinctKinds(documents.data ?? []), draft?.category.trim() || '']) {
      if (k && !seen.has(k.toLowerCase())) seen.set(k.toLowerCase(), k);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  })();

  // The sub-area select's options: values already in use within the
  // *selected* life area (plus the current draft value), alphabetical.
  // Purely per-area — an area nobody has subdivided offers just
  // None / Other…, which is how the feature stays dormant until needed.
  const subAreaOptions = (() => {
    if (!draft) return [];
    const inArea = (documents.data ?? []).filter((d) => d.lifeAreaId === draft.lifeAreaId);
    const seen = new Map<string, string>();
    for (const s of distinctSubAreas(inArea)) seen.set(s.toLowerCase(), s);
    const current = draft.subArea.trim();
    if (current && !seen.has(current.toLowerCase())) seen.set(current.toLowerCase(), current);
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  })();

  useEffect(() => {
    if (target === null) {
      setDraft(null);
    } else if (target === 'new') {
      const fresh = emptyDraft(defaultAreaId, vault, presetTripId);
      // A file dropped onto the page / picked from the capture sheet
      // seeds the title so the record can be saved straight away.
      if (initialFile) fresh.title = titleFromFileName(initialFile.name);
      setDraft(fresh);
    } else {
      setDraft(toDraft(target));
    }
    // A file staged before open pre-attaches: to a fresh record
    // (drag-drop / capture on the page) or to an existing one (a file
    // dropped on its open read card — "attach to this record"). Plain
    // edits pass no initialFile and start clean.
    setStagedFile(initialFile ?? null);
    setFileError(null);
    setFileDragOver(false);
    setReading(false);
    setAiFilled(false);
    setAiNote(null);
    setSuggestedPeople([]);
    setCustomKind(false);
    setCustomSubArea(false);
    setConfirmingFileRemove(false);
    setSaving(false);
    setSaveStatus(null);
    committedId.current = null;
    setConfirmingDelete(false);
    setDragOffset(0);
    setDragging(false);
    dragStartY.current = null;
    // Keyed by the document *id*, not the object: the page hands us the
    // live cache row, whose identity changes on every cache write or
    // background refetch (e.g. the window-focus refetch after the OS
    // file picker). Reseeding on identity threw away everything
    // in-progress — form edits, the just-staged file, save state — and
    // whatever the reverted form showed was then saved over the record.
    // Only an actual subject change should reseed. (defaultAreaId also
    // intentionally excluded — it only matters when a 'new' draft is
    // seeded, and life areas load once.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target === 'new' ? 'new' : target?.id]);

  useEffect(() => {
    if (!target) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (confirmingDelete) setConfirmingDelete(false);
      else if (!saving) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, onClose, confirmingDelete, saving]);

  useEffect(() => {
    if (!target) return;
    document.body.classList.add('body--drawer-open');
    function blockTouchMove(e: TouchEvent) {
      const node = e.target as Node | null;
      const drawerBody = document.querySelector('.drawer__body');
      if (drawerBody && node && drawerBody.contains(node)) return;
      e.preventDefault();
    }
    document.addEventListener('touchmove', blockTouchMove, { passive: false });
    return () => {
      document.body.classList.remove('body--drawer-open');
      document.removeEventListener('touchmove', blockTouchMove);
    };
  }, [target]);

  function handleDragStart(e: React.TouchEvent<HTMLDivElement>) {
    if (e.touches.length !== 1) return;
    const node = e.target as HTMLElement;
    if (node.closest('button, input, textarea, select, label')) return;
    if (node.closest('.drawer__body')) return;
    dragStartY.current = e.touches[0]!.clientY;
    setDragging(true);
  }

  function handleDragMove(e: React.TouchEvent<HTMLDivElement>) {
    if (dragStartY.current === null) return;
    const delta = e.touches[0]!.clientY - dragStartY.current;
    setDragOffset(Math.max(0, delta));
  }

  function handleDragEnd() {
    if (dragStartY.current === null) return;
    dragStartY.current = null;
    setDragging(false);
    if (dragOffset > DRAG_DISMISS_PX && !saving) {
      setDragOffset(window.innerHeight);
      window.setTimeout(() => {
        onClose();
        setDragOffset(0);
      }, DRAG_CLOSE_ANIMATION_MS);
    } else {
      setDragOffset(0);
    }
  }

  if (!target || !draft) return null;

  // A staged file is always saveable — its title falls back to the
  // filename (see `save`), so an empty title field shouldn't block it.
  const saveDisabled = draft.title.trim().length === 0 && !stagedFile;

  // The file shown in the attach field: a freshly-staged file takes
  // precedence over whatever's already attached to the record.
  const existingFileName = existing?.fileName ?? null;
  const attachedName = stagedFile ? stagedFile.name : existingFileName;
  const attachedSub = stagedFile
    ? `${formatFileSize(stagedFile.size)} · uploads on save`
    : existingFileName
      ? 'Attached'
      : null;

  function save() {
    if (!draft) return;
    // Shared fields the drawer exposes. `documentCategory` is not a
    // drawer field — it's set only on create ('vital' when the drawer
    // was opened from the Vault, 'routine' otherwise) and left
    // untouched on update; the card's Move-to-Vault / Retire actions
    // own the flips. Omitting it from the patch means "leave
    // unchanged".
    // Title falls back to the staged filename so a file-first save never
    // ends up with an empty title.
    const title =
      draft.title.trim() || (stagedFile ? titleFromFileName(stagedFile.name) : '');
    if (title.length === 0) return;
    const fields = {
      title,
      lifeAreaId: draft.lifeAreaId,
      subArea: draft.subArea.trim() || null,
      category: draft.category.trim() || null,
      pertainsTo: draft.pertainsTo,
      issuer: draft.issuer.trim() || null,
      issuedDate: draft.issuedDate || null,
      expiryDate: draft.expiryDate || null,
      tags: parseTags(draft.tags),
      notes: draft.notes.trim() || null,
      visibility: draft.visibility,
      tripId: draft.tripId || null,
    };
    // The Vault toggle writes documentCategory only when it actually
    // moved — an untouched toggle on a Retired record must not flip it
    // back to routine on an ordinary save.
    const wasVault = existing ? existing.documentCategory === 'vital' : false;
    // Cadence rides along whenever the record is (becoming) a vault
    // record; leaving the Vault clears it back to the default.
    const cadence = draft.vault ? { vaultReviewMonths: draft.reviewMonths } : {};
    const patchFields =
      existing && draft.vault !== wasVault
        ? {
            ...fields,
            ...cadence,
            documentCategory: draft.vault ? ('vital' as const) : ('routine' as const),
          }
        : { ...fields, ...cadence };
    const createCategory = draft.vault ? ('vital' as const) : ('routine' as const);
    if (saving) return;
    const file = stagedFile;

    // No file: plain optimistic create/update — closes instantly. The
    // record persists even though the drawer unmounts (onMutate updates
    // the cache; the network write runs on the mutation cache).
    if (!file) {
      if (existing) {
        update.mutate({ id: existing.id, input: patchFields });
      } else {
        create.mutate({ id: crypto.randomUUID(), documentCategory: createCategory, ...fields, ...cadence });
      }
      onClose();
      return;
    }

    // With a file: keep the drawer open and mounted while we write the
    // record and upload the file. Mounted means the mutation hooks run to
    // completion — closing first would unmount them and silently drop the
    // upload. The button shows "Saving…"; we close only on success.
    if (!navigator.onLine) {
      setFileError('You’re offline — reconnect to attach the file.');
      return;
    }
    const id = existing ? existing.id : (committedId.current ?? crypto.randomUUID());
    const alreadyCommitted = Boolean(existing) || committedId.current === id;
    setFileError(null);
    setSaving(true);
    setSaveStatus('Saving record…');
    void (async () => {
      try {
        if (alreadyCommitted) {
          await withTimeout(update.mutateAsync({ id, input: patchFields }), 30000, 'save record');
        } else {
          await withTimeout(
            create.mutateAsync({ id, documentCategory: createCategory, ...fields, ...cadence }),
            30000,
            'save record',
          );
          committedId.current = id;
        }
        setSaveStatus('Uploading file…');
        await withTimeout(upload.mutateAsync({ id, file }), 60000, 'upload file');
        onClose();
      } catch (err) {
        console.error('Document save/upload failed', err);
        setSaving(false);
        setSaveStatus(null);
        setFileError(`Couldn’t save — ${describeError(err)}`);
      }
    })();
  }

  function pickFile(file: File | undefined | null) {
    if (!file) return;
    if (!isAcceptedDocFile(file)) {
      setFileError('Unsupported file — attach a PDF or an image.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setFileError('File is over 25 MB.');
      return;
    }
    setFileError(null);
    setStagedFile(file);
    // A fresh file invalidates any prior AI suggestions.
    setAiFilled(false);
    setAiNote(null);
    setSuggestedPeople([]);
    // Seed the title from the filename when it's still empty, so the
    // record is immediately saveable (and named something sensible).
    setDraft((d) => (d && d.title.trim() === '' ? { ...d, title: titleFromFileName(file.name) } : d));
  }

  // Read the staged file with Claude and pre-fill the form. Opt-in per
  // upload (the button), so nothing is sent unless the user asks.
  // Offered whenever a fresh file is staged — including on an existing
  // record, where a deliberate re-scan replaces the fields the AI can
  // read and leaves the rest as they were.
  async function readWithAI() {
    if (!stagedFile || reading) return;
    setAiNote(null);
    setReading(true);
    try {
      const result = await api.extractDocument(stagedFile);
      if (!result.configured) {
        setAiNote('AI reading isn’t set up yet.');
        return;
      }
      if (!result.supported) {
        setAiNote('This file type can’t be read — enter the details below.');
        return;
      }
      applySuggestion(result.suggestion, result.people);
      setAiFilled(true);
    } catch (err) {
      setAiNote(`Couldn’t read the file — ${describeError(err)}`);
    } finally {
      setReading(false);
    }
  }

  function applySuggestion(s: DocumentSuggestion, ppl: SuggestedPerson[]) {
    const matchedIds = ppl
      .map((p) => p.personId)
      .filter((id): id is string => Boolean(id));
    // An AI category is always from the standard list — make sure the
    // select (not the Other… input) is showing it.
    if (s.category) setCustomKind(false);
    if (s.subArea) setCustomSubArea(false);
    setDraft((d) => {
      if (!d) return d;
      const nextAreaId = s.lifeAreaId ?? d.lifeAreaId;
      return {
        ...d,
        title: s.title ?? d.title,
        // File it into the area Claude picked from the real list.
        lifeAreaId: nextAreaId,
        // A sub-area only survives within its own area: take Claude's
        // (validated server-side against the chosen area), else keep the
        // current one only if the area didn't change.
        subArea: s.subArea ?? (nextAreaId === d.lifeAreaId ? d.subArea : ''),
        category: s.category ?? d.category,
        issuer: s.issuer ?? d.issuer,
        issuedDate: s.issuedDate ?? d.issuedDate,
        expiryDate: s.expiryDate ?? d.expiryDate,
        tags: s.tags && s.tags.length > 0 ? s.tags.join(', ') : d.tags,
        notes: s.summary ?? d.notes,
        pertainsTo: [...new Set([...d.pertainsTo, ...matchedIds])],
      };
    });
    // Surface only the people we couldn't link as add-chips.
    setSuggestedPeople(ppl.filter((p) => !p.personId));
  }

  // Permanently delete the file attached to an existing record (keeping
  // the record). Takes effect immediately — no save needed.
  function removeAttachedFile() {
    if (!existing) return;
    removeFile.mutate({ id: existing.id });
    setConfirmingFileRemove(false);
  }

  // Create a person Claude named but couldn't match, and link it.
  function addSuggestedPerson(p: SuggestedPerson) {
    const id = crypto.randomUUID();
    createPerson.mutate({ id, displayName: p.name });
    setDraft((d) => (d ? { ...d, pertainsTo: [...new Set([...d.pertainsTo, id])] } : d));
    setSuggestedPeople((prev) => prev.filter((x) => x.name !== p.name));
  }

  // Don't let any dismiss path (scrim, Close, Cancel, Escape, drag) close
  // the drawer while a file is uploading — unmounting mid-upload would
  // drop it.
  function requestClose() {
    if (saving) return;
    onClose();
  }

  function remove() {
    if (!existing) return;
    const doc = existing;
    del.mutate({ id: doc.id });
    // Offer a brief window to change your mind — the delete is a soft
    // delete, so Undo simply restores the row (and its file).
    undo.show(`Deleted “${doc.title}”`, () => {
      restore.mutate(doc);
    });
    onClose();
  }

  return (
    <>
      <div className="drawer-scrim" onClick={requestClose} aria-hidden />
      <aside
        className={`drawer ${dragging ? 'drawer--dragging' : ''}`}
        style={{ ['--drawer-drag-y' as string]: `${dragOffset}px` }}
        role="dialog"
        aria-label={isNew ? 'Add document' : 'Edit document'}
      >
        <div
          className="drawer__grip"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onTouchCancel={handleDragEnd}
        >
          <div className="drawer__handle" aria-hidden />
          <header className="drawer__header">
            <h2 className="drawer__title">{isNew ? 'Add document' : 'Edit document'}</h2>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={requestClose}
              disabled={saving}
            >
              Close
            </button>
          </header>
        </div>

        <div className="drawer__body">
          {aiFilled && (
            <div className="doc-ai-banner" role="status">
              <SparkleIcon />
              Read from your file — check anything before saving.
            </div>
          )}

          <Field label="Title">
            <input
              className="field__input"
              type="text"
              name="title"
              value={draft.title}
              placeholder="Home insurance policy"
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Field>

          <div className="field">
            <PersonPicker
              label="Who is this about?"
              placeholder="Add a person — yourself, a child…"
              multi
              values={draft.pertainsTo}
              onValuesChange={(ids) => setDraft({ ...draft, pertainsTo: ids })}
            />
            <span className="field__hint">
              Who the document concerns. Type a name to add someone new.
            </span>
            {suggestedPeople.length > 0 && (
              <div className="doc-ai-people">
                <span className="doc-ai-people__label">Also mentioned</span>
                {suggestedPeople.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    className="doc-ai-people__chip"
                    onClick={() => addSuggestedPerson(p)}
                  >
                    + {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Field label="Life area" hint="Files it — Health, Travel, Household…">
            <select
              className="field__input field__input--select"
              name="life-area"
              value={draft.lifeAreaId}
              onChange={(e) => {
                // A sub-area belongs to its area — switching areas
                // clears it rather than dragging "Dentist" into Travel.
                setDraft({ ...draft, lifeAreaId: e.target.value, subArea: '' });
                setCustomSubArea(false);
              }}
            >
              {lifeAreas.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Sub-area" hint="Optional finer folder — Dentist, Optometrist…">
            <select
              className="field__input field__input--select"
              name="sub-area"
              value={customSubArea ? OTHER_SUB_AREA : draft.subArea}
              onChange={(e) => {
                const v = e.target.value;
                if (v === OTHER_SUB_AREA) {
                  setCustomSubArea(true);
                  setDraft({ ...draft, subArea: '' });
                } else {
                  setCustomSubArea(false);
                  setDraft({ ...draft, subArea: v });
                }
              }}
            >
              <option value="">None</option>
              {subAreaOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              <option value={OTHER_SUB_AREA}>Other…</option>
            </select>
            {customSubArea && (
              <input
                className="field__input field__input--stacked"
                type="text"
                name="sub-area-custom"
                value={draft.subArea}
                placeholder="New sub-area name"
                onChange={(e) => setDraft({ ...draft, subArea: e.target.value })}
              />
            )}
          </Field>

          <div className="field-row">
            <Field label="Category" hint="What kind — bill, insurance, tickets…">
              <select
                className="field__input field__input--select"
                name="category"
                value={customKind ? OTHER_KIND : draft.category}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === OTHER_KIND) {
                    // Escape hatch: switch to a free-text input for the
                    // rare document that doesn't fit the standard list.
                    setCustomKind(true);
                    setDraft({ ...draft, category: '' });
                  } else {
                    setCustomKind(false);
                    setDraft({ ...draft, category: v });
                  }
                }}
              >
                <option value="">None</option>
                {kindOptions.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
                <option value={OTHER_KIND}>Other…</option>
              </select>
              {customKind && (
                <input
                  className="field__input field__input--stacked"
                  type="text"
                  name="category-custom"
                  value={draft.category}
                  placeholder="New category name"
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                />
              )}
            </Field>
            <Field label="Issuer" hint="Who issued it — Allianz, État de Genève…">
              <input
                className="field__input"
                type="text"
                name="issuer"
                value={draft.issuer}
                onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
              />
            </Field>
          </div>

          <div className="field-row">
            <Field label="Issued">
              <input
                className="field__input"
                type="date"
                name="issued-date"
                value={draft.issuedDate}
                onChange={(e) => setDraft({ ...draft, issuedDate: e.target.value })}
              />
            </Field>
            <Field label="Expires" hint="Drives the “what’s expiring” view.">
              <input
                className="field__input"
                type="date"
                name="expiry-date"
                value={draft.expiryDate}
                onChange={(e) => setDraft({ ...draft, expiryDate: e.target.value })}
              />
            </Field>
          </div>

          <Field label="File" hint="Attach a PDF or photo — drag one here, or tap to choose.">
            {/* The label opens the native picker on tap (works in the iOS
                PWA); the input is visually hidden but kept in-flow and
                label-associated. Drag-and-drop lands on the label too. */}
            <label
              className={`file-drop ${fileDragOver ? 'file-drop--over' : ''}`}
              htmlFor={fileInputId}
              // stopPropagation throughout: the drawer renders inside the
              // Documents page, whose own drag handlers open a *new*
              // document for dropped files. Without it, dropping a file
              // here bubbled up, flipped the drawer to 'new', and blanked
              // the form mid-edit — read as "it wiped my metadata".
              onDragEnter={(e) => e.stopPropagation()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFileDragOver(true);
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                setFileDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFileDragOver(false);
                pickFile(e.dataTransfer.files?.[0]);
              }}
            >
              {attachedName ? (
                <span className="file-drop__file">
                  <FileGlyph />
                  <span className="file-drop__meta">
                    <span className="file-drop__name">{attachedName}</span>
                    {attachedSub && <span className="file-drop__sub">{attachedSub}</span>}
                  </span>
                  {(stagedFile || existingFileName) && (
                    <button
                      type="button"
                      className="file-drop__remove"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // A not-yet-saved pick just un-stages; an already
                        // attached file needs a confirm before deletion.
                        if (stagedFile) setStagedFile(null);
                        else setConfirmingFileRemove(true);
                      }}
                      aria-label={stagedFile ? 'Remove staged file' : 'Remove attached file'}
                    >
                      ×
                    </button>
                  )}
                </span>
              ) : (
                <span className="file-drop__prompt">
                  <FileGlyph />
                  Drag a file here, or tap to choose
                </span>
              )}
              <input
                id={fileInputId}
                className="file-drop__input"
                type="file"
                accept={DOC_FILE_ACCEPT}
                onChange={(e) => {
                  pickFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {confirmingFileRemove && (
              <div className="file-remove-confirm" role="alertdialog" aria-label="Confirm remove file">
                <span className="file-remove-confirm__message">
                  Remove this file? It will be permanently deleted. The document and its
                  details stay.
                </span>
                <div className="file-remove-confirm__actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => setConfirmingFileRemove(false)}
                  >
                    Keep
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    onClick={removeAttachedFile}
                  >
                    Remove file
                  </button>
                </div>
              </div>
            )}
            {stagedFile && (
              <>
                <button
                  type="button"
                  className="doc-ai-read"
                  onClick={readWithAI}
                  disabled={reading}
                >
                  <SparkleIcon />
                  {reading ? 'Reading…' : aiFilled ? 'Read again' : 'Read with AI'}
                </button>
                <span className="field__hint">
                  Let Claude read the file and fill in the details. Stored privately —
                  only its contents are read.
                </span>
              </>
            )}
            {aiNote && <span className="field__hint field__hint--error">{aiNote}</span>}
            {fileError && <span className="field__hint field__hint--error">{fileError}</span>}
          </Field>

          <Field label="Tags" hint="Comma-separated.">
            <input
              className="field__input"
              type="text"
              name="tags"
              value={draft.tags}
              placeholder="home, annual"
              onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
            />
          </Field>

          {/* Vault membership is a binary, so it's a checkbox — same
              row pattern as the person editor's Household flag. Ticking
              it nudges Sharing to shared in the same gesture (a visible
              default, not a lock — the Sharing field below flips it
              straight back). */}
          <label className="field person-household">
            <input
              type="checkbox"
              className="person-household__box"
              name="vault"
              checked={draft.vault}
              onChange={(e) => {
                const toVault = e.target.checked;
                setDraft({
                  ...draft,
                  vault: toVault,
                  visibility:
                    toVault && draft.visibility !== 'shared' ? 'shared' : draft.visibility,
                });
              }}
            />
            <span className="person-household__mid">
              <span className="person-household__label">Keep in the Vault</span>
              <span className="field__hint person-household__hint">
                Vital record (passport, birth certificate, will) — a second Dropbox copy
                files under /Vault and it joins the review cycle below.
              </span>
            </span>
          </label>

          {draft.vault && (
            <Field label="Review" hint={reviewHint(existing, draft.reviewMonths)}>
              <select
                className="field__input field__input--select"
                name="vaultReviewMonths"
                value={String(draft.reviewMonths)}
                onChange={(e) =>
                  setDraft({ ...draft, reviewMonths: Number(e.target.value) })
                }
              >
                <option value="6">Every 6 months</option>
                <option value="12">Every year</option>
                <option value="24">Every 2 years</option>
              </select>
            </Field>
          )}

          <Field
            label="Sharing"
            hint={
              draft.visibility === 'shared'
                ? 'Visible to everyone in your household.'
                : 'Private to you. Other members cannot see it.'
            }
          >
            <select
              className="field__input field__input--select"
              name="visibility"
              value={draft.visibility === 'shared' ? 'shared' : 'private'}
              onChange={(e) =>
                setDraft({ ...draft, visibility: e.target.value as DocumentVisibility })
              }
            >
              <option value="private">Private to me</option>
              <option value="shared">Shared with household</option>
            </select>
          </Field>

          {tripOptions.length > 0 && (
            <Field
              label="Trip"
              hint="Linked documents show on the trip and stay on this device until it ends."
            >
              <select
                className="field__input field__input--select"
                name="tripId"
                value={draft.tripId}
                onChange={(e) => setDraft({ ...draft, tripId: e.target.value })}
              >
                <option value="">No trip</option>
                {tripOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Notes">
            <textarea
              className="field__input field__input--textarea"
              name="notes"
              rows={4}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </Field>

          {existing && (
            <div className="drawer__meta">
              <span>Added {formatStamp(existing.createdAt)}</span>
              <span>Last updated {formatStamp(existing.updatedAt)}</span>
            </div>
          )}
        </div>

        <footer className="drawer__footer">
          {existing && confirmingDelete ? (
            <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
              <span className="drawer__confirm-message">
                Delete this document?
                <span className="drawer__confirm-detail">
                  &ldquo;{existing.title}&rdquo; will be removed from the index. The file
                  itself isn&rsquo;t touched.
                </span>
              </span>
              <div className="drawer__confirm-actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => setConfirmingDelete(false)}
                  autoFocus
                >
                  Keep
                </button>
                <button type="button" className="btn btn--primary btn--small" onClick={remove}>
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <>
              {existing ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete
                </button>
              ) : (
                <span />
              )}
              <div className="drawer__footer-actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={requestClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={save}
                  disabled={saveDisabled || saving}
                >
                  {saving ? (saveStatus ?? 'Saving…') : 'Save'}
                </button>
              </div>
            </>
          )}
        </footer>
      </aside>
    </>
  );
}

function FileGlyph() {
  return (
    <svg
      className="file-drop__icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 3v5h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
